import debounce from "lodash.debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ModalAtom, TextFieldAtom } from "../../../components/atoms/atom";
import { useLazySearchCompaniesQuery } from "../../../store/services/projectCompaniesService";
import "./add-project-company-modal.scss";

const SUGGEST_MIN_LENGTH = 2;
const SUGGEST_LIMIT = 8;

const EMPTY_FORM = {
	name: "",
	addressStreet: "",
	addressZipCode: "",
	addressCity: "",
	addressCountry: "",
	taxId: "",
	phoneNumber: "",
};

const formatCompanyAddress = (companyRow) => {
	const parts = [
		companyRow.addressStreet,
		companyRow.addressZipCode,
		companyRow.addressCity,
		companyRow.addressCountry,
	].filter(Boolean);
	return parts.join(", ");
};

/**
 * Modal for creating a project-only company inline. As the name is typed it
 * searches existing master-data companies (mirrors the invite wizard's email
 * typeahead): picking a suggestion links that existing company instead of
 * creating a duplicate.
 *
 * A same-name match does NOT hard-block creation: on save we show an inline
 * warning that lets the user either link the existing company or deliberately
 * create a separate one (e.g. same name at a different PLZ). "Create separately"
 * sends `allowDuplicateName` so the server's duplicate guard is bypassed.
 */
function AddProjectCompanyModal({
	show,
	onClose,
	onSubmit,
	loading = false,
	existingCompanyIds = [],
	existingCompanyNames = [],
	onSelectExisting,
}) {
	const { t } = useTranslation();

	const [formData, setFormData] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState({});
	const [searchResults, setSearchResults] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [highlightIndex, setHighlightIndex] = useState(-1);
	// Inline same-name confirmation: null | { mode: "master" | "linked", match? }
	const [dupWarning, setDupWarning] = useState(null);

	const suggestRef = useRef(null);
	const skipSuggestRef = useRef(false);

	const [triggerSearch] = useLazySearchCompaniesQuery();

	const existingIdSet = useMemo(
		() => new Set(existingCompanyIds),
		[existingCompanyIds],
	);

	const existingNameSet = useMemo(
		() =>
			new Set(
				existingCompanyNames.map((n) =>
					String(n).trim().toLowerCase(),
				),
			),
		[existingCompanyNames],
	);

	const suggestions = useMemo(
		() =>
			formData.name.trim().length < SUGGEST_MIN_LENGTH
				? []
				: searchResults.slice(0, SUGGEST_LIMIT),
		[formData.name, searchResults],
	);

	const runSuggestSearch = useMemo(
		() =>
			debounce(async (value) => {
				const query = value.trim();
				if (query.length < SUGGEST_MIN_LENGTH) {
					setSearchResults([]);
					return;
				}
				try {
					const res = await triggerSearch({
						search: query,
						limit: SUGGEST_LIMIT,
					}).unwrap();
					setSearchResults(res?.items ?? []);
				} catch {
					setSearchResults([]);
				}
			}, 250),
		[triggerSearch],
	);

	useEffect(() => () => runSuggestSearch.cancel(), [runSuggestSearch]);

	useEffect(() => {
		const onDocClick = (event) => {
			if (
				suggestRef.current &&
				!suggestRef.current.contains(event.target)
			) {
				setShowSuggestions(false);
				setHighlightIndex(-1);
			}
		};
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, []);

	const resetForm = () => {
		setFormData(EMPTY_FORM);
		setErrors({});
		setSearchResults([]);
		setShowSuggestions(false);
		setHighlightIndex(-1);
		setDupWarning(null);
		runSuggestSearch.cancel();
	};

	// Clear the form whenever the modal is hidden, so reopening always starts
	// blank. This covers every close path - the X button, a successful create,
	// and picking an existing company - since they all flip `show` to false
	// (some without routing through handleClose).
	useEffect(() => {
		if (!show) {
			resetForm();
		}
		// resetForm is stable enough for this guard; runSuggestSearch is memoized.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const onNameChange = (e) => {
		const value = e.target.value;
		handleChange("name", value);
		// The name drives the duplicate check, so a fresh name invalidates any
		// pending same-name confirmation.
		setDupWarning(null);
		runSuggestSearch.cancel();

		if (skipSuggestRef.current) {
			skipSuggestRef.current = false;
			return;
		}

		const trimmed = value.trim();
		if (trimmed.length < SUGGEST_MIN_LENGTH) {
			setSearchResults([]);
			setShowSuggestions(false);
			setHighlightIndex(-1);
			return;
		}
		setShowSuggestions(true);
		setHighlightIndex(-1);
		runSuggestSearch(trimmed);
	};

	const isSuggestionDisabled = (companyRow) =>
		existingIdSet.has(companyRow.id);

	const selectSuggestion = (companyRow) => {
		if (isSuggestionDisabled(companyRow)) {
			return;
		}
		// Picking an existing company links it to the project rather than
		// creating a duplicate. The parent handles the link + close.
		skipSuggestRef.current = true;
		setShowSuggestions(false);
		setHighlightIndex(-1);
		runSuggestSearch.cancel();
		onSelectExisting?.(companyRow.id);
	};

	const onNameKeyDown = (e) => {
		if (!showSuggestions || suggestions.length === 0) {
			return;
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightIndex((prev) =>
				prev < suggestions.length - 1 ? prev + 1 : 0,
			);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightIndex((prev) =>
				prev > 0 ? prev - 1 : suggestions.length - 1,
			);
		} else if (e.key === "Enter" && highlightIndex >= 0) {
			e.preventDefault();
			selectSuggestion(suggestions[highlightIndex]);
		} else if (e.key === "Escape") {
			setShowSuggestions(false);
			setHighlightIndex(-1);
		}
	};

	const validateForm = () => {
		const newErrors = {};
		if (!formData.name || formData.name.trim() === "") {
			newErrors.name = "projects.companies.error-company-name-required";
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const buildCleanedData = () =>
		Object.entries(formData).reduce((acc, [key, value]) => {
			acc[key] = value && value.trim() !== "" ? value.trim() : null;
			return acc;
		}, {});

	// Create after the same-name confirmation (or when there is no collision).
	// allowDuplicateName tells the server to skip its duplicate guard.
	const submitCreate = (allowDuplicateName) => {
		setDupWarning(null);
		onSubmit(buildCleanedData(), allowDuplicateName);
	};

	// Find a same-name company the user could link instead of duplicating:
	//  - "linked": a company with this name is already on this project
	//  - "master": a shared master company matches (returned by the name search)
	// Returns null when there is no collision and creation can proceed directly.
	const detectDuplicate = async (trimmedName) => {
		const lowerName = trimmedName.toLowerCase();

		if (existingNameSet.has(lowerName)) {
			return { mode: "linked" };
		}

		if (trimmedName.length < SUGGEST_MIN_LENGTH) {
			return null;
		}

		try {
			const res = await triggerSearch({
				search: trimmedName,
				limit: SUGGEST_LIMIT,
			}).unwrap();
			const match = (res?.items ?? []).find(
				(c) =>
					String(c.name).trim().toLowerCase() === lowerName &&
					!existingIdSet.has(c.id),
			);
			if (match) {
				return { mode: "master", match };
			}
		} catch {
			// A search failure shouldn't block creation; the server guard still
			// catches a genuine duplicate as a backstop.
		}
		return null;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}
		const trimmedName = formData.name.trim();
		const duplicate = await detectDuplicate(trimmedName);
		if (duplicate) {
			setShowSuggestions(false);
			setDupWarning(duplicate);
			return;
		}
		submitCreate(false);
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const showSuggestList =
		showSuggestions &&
		formData.name.trim().length >= SUGGEST_MIN_LENGTH &&
		suggestions.length > 0;

	return (
		<ModalAtom
			show={show}
			handleClose={handleClose}
			title={t("projects.companies.add-project-company")}
			saveText={loading ? "common.saving" : "common.save"}
			handleSubmit={handleSubmit}
			disabled={loading}
			submitDisabled={!!dupWarning}
			customClassName="add-project-company-modal"
			size="lg"
			body={
				<div className="px-3">
					<div className="mb-3 alert alert-info">
						<i className="bi bi-info-circle me-2"></i>
						{t("projects.companies.project-company-info")}
					</div>

					<Row>
						<Col md={12}>
							<div
								className="apc-name-suggest"
								ref={suggestRef}
							>
								<TextFieldAtom
									label={t("companies.company-name")}
									value={formData.name}
									onChange={onNameChange}
									onFocus={() => {
										if (
											formData.name.trim().length >=
											SUGGEST_MIN_LENGTH
										) {
											setShowSuggestions(true);
										}
									}}
									onKeyDown={onNameKeyDown}
									error={errors.name}
									required
									autoComplete="off"
									placeholder={t(
										"companies.company-name-search-placeholder",
									)}
								/>
								{showSuggestList && (
									<ul
										className="apc-suggest-list"
										role="listbox"
									>
										{suggestions.map((companyRow, index) => {
											const disabled =
												isSuggestionDisabled(
													companyRow,
												);
											const address =
												formatCompanyAddress(
													companyRow,
												);
											return (
												<li
													key={companyRow.id}
													role="option"
													aria-selected={
														index === highlightIndex
													}
													aria-disabled={disabled}
													className={`${index === highlightIndex ? "active" : ""}${disabled ? " is-disabled" : ""}`}
													onMouseDown={(e) => {
														e.preventDefault();
														selectSuggestion(
															companyRow,
														);
													}}
												>
													<span className="apc-suggest-name">
														{companyRow.name}
													</span>
													{address && (
														<span className="apc-suggest-meta">
															{address}
														</span>
													)}
													{disabled && (
														<span className="apc-suggest-badge">
															{t(
																"projects.companies.suggest-already-added",
															)}
														</span>
													)}
												</li>
											);
										})}
									</ul>
								)}
							</div>
							{showSuggestList && (
								<p className="apc-suggest-hint text-muted">
									{t(
										"projects.companies.company-suggest-hint",
									)}
								</p>
							)}
						</Col>
					</Row>

					{dupWarning && (
						<div className="apc-dup-warning" role="alert">
							<div className="apc-dup-warning__text">
								<i className="bi bi-exclamation-triangle-fill me-2"></i>
								{dupWarning.mode === "linked"
									? t(
											"projects.companies.duplicate-warning-linked",
											{ name: formData.name.trim() },
										)
									: t(
											"projects.companies.duplicate-warning-master",
											{ name: formData.name.trim() },
										)}
								{dupWarning.mode === "master" &&
									formatCompanyAddress(dupWarning.match) && (
										<span className="apc-dup-warning__addr">
											{formatCompanyAddress(
												dupWarning.match,
											)}
										</span>
									)}
							</div>
							<div className="apc-dup-warning__actions">
								{dupWarning.mode === "master" && (
									<button
										type="button"
										className="btn btn-sm btn-outline-primary"
										onClick={() =>
											selectSuggestion(dupWarning.match)
										}
									>
										{t(
											"projects.companies.duplicate-link-existing",
										)}
									</button>
								)}
								<button
									type="button"
									className="btn btn-sm btn-primary"
									onClick={() => submitCreate(true)}
								>
									{t(
										"projects.companies.duplicate-create-separate",
									)}
								</button>
								<button
									type="button"
									className="btn btn-sm btn-link"
									onClick={() => setDupWarning(null)}
								>
									{t("common.cancel")}
								</button>
							</div>
						</div>
					)}

					<Row>
						<Col md={12}>
							<TextFieldAtom
								label={t("companies.address-street")}
								value={formData.addressStreet}
								onChange={(e) =>
									handleChange(
										"addressStreet",
										e.target.value,
									)
								}
								placeholder={t(
									"companies.enter-street-address",
								)}
							/>
						</Col>
					</Row>

					<Row>
						<Col md={4}>
							<TextFieldAtom
								label={t("companies.zip-code")}
								value={formData.addressZipCode}
								onChange={(e) =>
									handleChange(
										"addressZipCode",
										e.target.value,
									)
								}
								placeholder={t("companies.enter-zip-code")}
							/>
						</Col>
						<Col md={8}>
							<TextFieldAtom
								label={t("companies.city")}
								value={formData.addressCity}
								onChange={(e) =>
									handleChange("addressCity", e.target.value)
								}
								placeholder={t("companies.enter-city")}
							/>
						</Col>
					</Row>

					<Row>
						<Col md={6}>
							<TextFieldAtom
								label={t("companies.country")}
								value={formData.addressCountry}
								onChange={(e) =>
									handleChange(
										"addressCountry",
										e.target.value,
									)
								}
								placeholder={t("companies.enter-country")}
							/>
						</Col>
						<Col md={6}>
							<TextFieldAtom
								label={t("companies.tax-id")}
								value={formData.taxId}
								onChange={(e) =>
									handleChange("taxId", e.target.value)
								}
								placeholder={t("companies.enter-tax-id")}
							/>
						</Col>
					</Row>

					<Row>
						<Col md={12}>
							<TextFieldAtom
								label={t("companies.phone-number")}
								value={formData.phoneNumber}
								onChange={(e) =>
									handleChange("phoneNumber", e.target.value)
								}
								placeholder={t("companies.enter-phone-number")}
							/>
						</Col>
					</Row>
				</div>
			}
		/>
	);
}

export default AddProjectCompanyModal;
